import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUserRole } from "../../security/roleManager";
import { ROLES } from "../../security/roles";
import API from "../../config/api";
import DemoDataNotice from "../../components/shared/DemoDataNotice";
import LanguageToggle from "../../components/shared/LanguageToggle";
import SubPageNav from "../../components/shared/SubPageNav";

const PAID_ROLES = [ROLES.CUSTOMER_USER, ROLES.CUSTOMER_ADMIN, ROLES.FENTASAFE_ADMIN];

// Demo-mode fallback — mirrors the shape returned by
// GET /api/research-validation (see fentasafe-backend-V2/app/routers/research_validation.py)
// so demo users see the same layout paid/live users see against real data.
// The patent/collaboration fields intentionally do NOT reuse the real backend's
// specific claims (patent number, institution name) in this illustrative context.
function generateSimulatedResearchData() {
  const riskDistribution = { CRITICAL: 34, WARNING: 156, ELEVATED: 203, SAFE: 449 };
  const total = Object.values(riskDistribution).reduce((a, b) => a + b, 0);
  const highRisk = riskDistribution.CRITICAL + riskDistribution.WARNING;

  return {
    total_assessments: total,
    risk_distribution: riskDistribution,
    high_risk_rate_pct: Math.round((highRisk / total) * 1000) / 10,
    model_confidence: {
      average_pct: 87.4,
      confidence_trend: "+3.2% vs. early period",
    },
    operator_validation: {
      total_feedback_events: 312,
      confirmed: 268,
      false_positives: 19,
      false_negatives: 8,
      escalated: 17,
      false_positive_rate_pct: 6.1,
      operator_confirmation_rate_pct: 91.3,
    },
    signal_analysis: {
      top_risk_drivers: [
        { signal: "pm25", count: 389, pct: 46.2 },
        { signal: "voc", count: 301, pct: 35.7 },
        { signal: "fentanyl_ppb", count: 152, pct: 18.1 },
      ],
      memory_pattern_detection: { repeating_patterns: 96 },
    },
    adaptive_learning: {
      sensitivity_weights: { pm25: 1.04, voc: 0.97, fentanyl_ppb: 1.11 },
      learning_state_persisted: true,
    },
    dataset_metadata: {
      first_assessment: "2026-06-01 08:00 UTC",
      latest_assessment: "2026-09-14 21:00 UTC",
    },
    research_summary: {
      validation_status: "ACTIVE",
      evidence_records: total,
      explainability_status: "SHAP_ACTIVE",
      adaptive_learning_status: "PERSISTENT",
      // Canonical keys, not the real backend's literal claims — translated
      // at render time; a real (live) backend value simply won't match a key
      // and falls back to displaying its own raw text untranslated.
      patent_application: "availableInLiveMode",
      research_collaboration: "availableInLiveMode",
      note: "simulatedNote",
    },
  };
}

export default function ResearchValidation() {
  const { t } = useTranslation();
  const role = getUserRole();
  const isLive = PAID_ROLES.includes(role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Demo users: use local simulated data, matching every other page's
    // pattern, instead of calling a real backend that may not be reachable.
    if (!isLive) {
      setData(generateSimulatedResearchData());
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const token = localStorage.getItem("FENTASAFE_TOKEN");
        const res = await fetch(`${API}/api/research-validation`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`API error: ${res.status}`);
        const json = await res.json();
        setData(json);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isLive]);

  const card = { background: "#111827", padding: "20px", borderRadius: "14px", border: "1px solid #1e293b" };

  const riskColor = (r) =>
    r === "CRITICAL" ? "#dc2626" : r === "WARNING" ? "#f59e0b" : r === "ELEVATED" ? "#38bdf8" : "#22c55e";

  return (
    <div style={{ padding: "20px", color: "white" }}>

      {/* HEADER */}
      <div style={{ position: "relative", textAlign: "center", marginBottom: 24 }}>
          <h1 style={{ marginBottom: 6 }}>{t("researchValidation.title")}</h1>
          <p style={{ color: "#94a3b8", fontSize: "0.85rem", marginBottom: 0 }}>
            {t("researchValidation.subtitlePrefix")} {isLive ? t("researchValidation.subtitleLive") : t("researchValidation.subtitleDemo")}
          </p>
        <div style={{ position: "absolute", top: 0, right: 0, display: "flex", alignItems: "flex-start", gap: "12px" }}>
          <LanguageToggle />
          <div style={{
            padding: "6px 14px", borderRadius: 8,
            background: isLive ? "rgba(22,163,74,0.15)" : "rgba(251,191,36,0.1)",
            border: `1px solid ${isLive ? "rgba(22,163,74,0.4)" : "rgba(251,191,36,0.3)"}`,
            textAlign: "center", fontSize: "0.8rem",
          }}>
            <div style={{ color: "#64748b", fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: 1 }}>{t("commandCenter.mode")}</div>
            <div style={{ fontWeight: 700, color: isLive ? "#22c55e" : "#fbbf24" }}>{isLive ? t("commandCenter.modeLive") : t("commandCenter.modeDemo")}</div>
          </div>
        </div>
      </div>

      {loading && <p style={{ color: "#94a3b8" }}>{t("researchValidation.loading")}</p>}
      {error && <p style={{ color: "#f87171" }}>{t("researchValidation.errorPrefix")} {error}</p>}

      {data && data.status !== "NO_DATA" && (
        <>
          {/* TOP METRICS */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
            <div style={card}>
              <h3 style={{ color: "#94a3b8", fontSize: "0.85rem" }}>{t("researchValidation.metrics.totalAssessments")}</h3>
              <h2 style={{ color: "#38bdf8" }}>{data.total_assessments?.toLocaleString()}</h2>
              <p style={{ color: "#64748b", fontSize: "0.8rem" }}>{t("researchValidation.metrics.totalAssessmentsSubtitle")}</p>
              {!isLive && <DemoDataNotice textKey="common.demoDataNotice.simulatedResearchData" />}
            </div>
            <div style={card}>
              <h3 style={{ color: "#94a3b8", fontSize: "0.85rem" }}>{t("researchValidation.metrics.avgConfidence")}</h3>
              <h2 style={{ color: "#60a5fa" }}>{data.model_confidence?.average_pct}%</h2>
              <p style={{ color: "#64748b", fontSize: "0.8rem" }}>{t("researchValidation.metrics.avgConfidenceSubtitle")}</p>
            </div>
            <div style={card}>
              <h3 style={{ color: "#94a3b8", fontSize: "0.85rem" }}>{t("researchValidation.metrics.operatorFeedback")}</h3>
              <h2 style={{ color: "#22c55e" }}>{data.operator_validation?.total_feedback_events}</h2>
              <p style={{ color: "#64748b", fontSize: "0.8rem" }}>{t("researchValidation.metrics.operatorFeedbackSubtitle")}</p>
            </div>
            <div style={card}>
              <h3 style={{ color: "#94a3b8", fontSize: "0.85rem" }}>{t("researchValidation.metrics.highRiskRate")}</h3>
              <h2 style={{ color: data.high_risk_rate_pct > 20 ? "#ef4444" : "#f59e0b" }}>
                {data.high_risk_rate_pct}%
              </h2>
              <p style={{ color: "#64748b", fontSize: "0.8rem" }}>{t("researchValidation.metrics.highRiskRateSubtitle")}</p>
            </div>
          </div>

          {/* RISK DISTRIBUTION */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
            <div style={card}>
              <h3 style={{ marginBottom: 16 }}>{t("researchValidation.riskDistribution.title")}</h3>
              {Object.entries(data.risk_distribution || {}).map(([level, count]) => (
                <div key={level} style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, alignItems: "center" }}>
                  <span style={{ color: riskColor(level), fontWeight: 600 }}>{t(`commandCenter.risk.${level}`, level)}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 100, height: 6, background: "#1e293b", borderRadius: 3 }}>
                      <div style={{ width: `${Math.min((count / data.total_assessments) * 100, 100)}%`, height: "100%", background: riskColor(level), borderRadius: 3 }} />
                    </div>
                    <span style={{ color: "#94a3b8", minWidth: 30, textAlign: "right" }}>{count}</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={card}>
              <h3 style={{ marginBottom: 16 }}>{t("researchValidation.operatorValidation.title")}</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#94a3b8" }}>{t("researchValidation.operatorValidation.confirmed")}</span>
                  <span style={{ color: "#22c55e", fontWeight: 600 }}>{data.operator_validation?.confirmed}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#94a3b8" }}>{t("researchValidation.operatorValidation.falsePositives")}</span>
                  <span style={{ color: "#ef4444", fontWeight: 600 }}>{data.operator_validation?.false_positives}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#94a3b8" }}>{t("researchValidation.operatorValidation.falseNegatives")}</span>
                  <span style={{ color: "#f59e0b", fontWeight: 600 }}>{data.operator_validation?.false_negatives}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#94a3b8" }}>{t("researchValidation.operatorValidation.escalated")}</span>
                  <span style={{ color: "#38bdf8", fontWeight: 600 }}>{data.operator_validation?.escalated}</span>
                </div>
                <div style={{ marginTop: 8, padding: "8px 12px", background: "#0f172a", borderRadius: 8, fontSize: "0.8rem", color: "#94a3b8" }}>
                  {t("researchValidation.operatorValidation.fpRate")} <strong style={{ color: data.operator_validation?.false_positive_rate_pct > 20 ? "#ef4444" : "#22c55e" }}>
                    {data.operator_validation?.false_positive_rate_pct}%
                  </strong>
                  &nbsp;&nbsp;{t("researchValidation.operatorValidation.confirmation")} <strong style={{ color: "#22c55e" }}>
                    {data.operator_validation?.operator_confirmation_rate_pct}%
                  </strong>
                </div>
              </div>
            </div>
          </div>

          {/* SIGNAL ANALYSIS + LEARNING */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
            <div style={card}>
              <h3 style={{ marginBottom: 16 }}>{t("researchValidation.signalDrivers.title")}</h3>
              {(data.signal_analysis?.top_risk_drivers || []).map((d, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ color: "#cbd5e1" }}>{d.signal}</span>
                  <span style={{ color: "#60a5fa", fontWeight: 600 }}>{d.count} ({d.pct}%)</span>
                </div>
              ))}
              <div style={{ marginTop: 12, fontSize: "0.8rem", color: "#64748b" }}>
                {t("researchValidation.signalDrivers.memoryPatterns")} <strong style={{ color: "#94a3b8" }}>
                  {data.signal_analysis?.memory_pattern_detection?.repeating_patterns}
                </strong>
              </div>
            </div>

            <div style={card}>
              <h3 style={{ marginBottom: 16 }}>{t("researchValidation.adaptiveLearning.title")}</h3>
              {Object.entries(data.adaptive_learning?.sensitivity_weights || {}).map(([signal, weight]) => (
                <div key={signal} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ color: "#94a3b8", textTransform: "capitalize" }}>{signal}</span>
                  <span style={{ color: Math.abs(weight - 1.0) > 0.05 ? "#f59e0b" : "#22c55e", fontWeight: 600 }}>
                    {weight} {Math.abs(weight - 1.0) > 0.05 ? t("researchValidation.adaptiveLearning.adjusted") : t("researchValidation.adaptiveLearning.baseline")}
                  </span>
                </div>
              ))}
              <div style={{ marginTop: 12, fontSize: "0.8rem", color: "#64748b" }}>
                {t("researchValidation.adaptiveLearning.persistence")} <strong style={{ color: data.adaptive_learning?.learning_state_persisted ? "#22c55e" : "#f59e0b" }}>
                  {data.adaptive_learning?.learning_state_persisted ? t("researchValidation.adaptiveLearning.mongoPersisted") : t("researchValidation.adaptiveLearning.inMemoryOnly")}
                </strong>
              </div>
            </div>
          </div>

          {/* RESEARCH SUMMARY */}
          <div style={{ ...card, background: "rgba(37,99,235,0.06)", border: "1px solid rgba(37,99,235,0.2)" }}>
            <h3 style={{ marginBottom: 12, color: "#93c5fd" }}>{t("researchValidation.researchSummary.title")}</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, fontSize: "0.88rem" }}>
              <div><span style={{ color: "#64748b" }}>{t("researchValidation.researchSummary.validationStatus")}</span> <strong style={{ color: "#22c55e" }}>{data.research_summary?.validation_status}</strong></div>
              <div><span style={{ color: "#64748b" }}>{t("researchValidation.researchSummary.evidenceRecords")}</span> <strong>{data.research_summary?.evidence_records}</strong></div>
              <div><span style={{ color: "#64748b" }}>{t("researchValidation.researchSummary.explainability")}</span> <strong style={{ color: "#22c55e" }}>{data.research_summary?.explainability_status}</strong></div>
              <div><span style={{ color: "#64748b" }}>{t("researchValidation.researchSummary.adaptiveLearning")}</span> <strong>{data.research_summary?.adaptive_learning_status}</strong></div>
              <div><span style={{ color: "#64748b" }}>{t("researchValidation.researchSummary.patent")}</span> <strong style={{ color: "#93c5fd" }}>{t(`researchValidation.researchSummary.${data.research_summary?.patent_application}`, data.research_summary?.patent_application)}</strong></div>
              <div><span style={{ color: "#64748b" }}>{t("researchValidation.researchSummary.researchCollaboration")}</span> <strong style={{ color: "#93c5fd" }}>{t(`researchValidation.researchSummary.${data.research_summary?.research_collaboration}`, data.research_summary?.research_collaboration)}</strong></div>
            </div>
            <p style={{ marginTop: 12, fontSize: "0.78rem", color: "#475569", fontStyle: "italic" }}>
              {t(`researchValidation.researchSummary.${data.research_summary?.note}`, data.research_summary?.note)}
            </p>
          </div>

          {/* DATASET METADATA */}
          <div style={{ ...card, marginTop: 16 }}>
            <h3 style={{ marginBottom: 12 }}>{t("researchValidation.datasetMetadata.title")}</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, fontSize: "0.85rem" }}>
              <div><span style={{ color: "#64748b" }}>{t("researchValidation.datasetMetadata.firstAssessment")}</span><br /><strong>{data.dataset_metadata?.first_assessment}</strong></div>
              <div><span style={{ color: "#64748b" }}>{t("researchValidation.datasetMetadata.latestAssessment")}</span><br /><strong>{data.dataset_metadata?.latest_assessment}</strong></div>
              <div><span style={{ color: "#64748b" }}>{t("researchValidation.datasetMetadata.confidenceTrend")}</span><br /><strong style={{ color: data.model_confidence?.confidence_trend?.startsWith("+") ? "#22c55e" : "#ef4444" }}>{data.model_confidence?.confidence_trend}</strong></div>
            </div>
          </div>
        </>
      )}

      {data && data.status === "NO_DATA" && (
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: "2rem", marginBottom: 16 }}>📊</div>
          <h3>{t("researchValidation.noData.title")}</h3>
          <p style={{ color: "#94a3b8" }}>{data.message}</p>
        </div>
      )}

      <SubPageNav current="researchValidation" />

    </div>
  );
}
